import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { environment } from '../environments/environment';
import { AuthService } from './auth.service';
import { ClientErrorLogService } from './client-error-log.service';
import { collectMemory } from './client-memory';
import type { ClientCrashDetails, ClientErrorWho, ClientMemory } from './types/client-error';

/** Holds one record per session id, so several tabs of the same origin do not clobber each other. */
const SENTINEL_KEY = 'smileart.sessionSentinel.v1';

/** How often a live session says it is still there. */
const HEARTBEAT_INTERVAL_MS = 5_000;

/**
 * How long a leftover record is watched before it is called a crash.
 *
 * A background tab is throttled to roughly one timer callback per minute, so a session that is merely
 * backgrounded still advances its record inside this window. Anything that has not moved by the end of
 * it is not running any more.
 */
const CONFIRMATION_DELAY_MS = 75_000;

/** Records older than this say nothing useful about why a session ended, so they are discarded. */
const MAX_RECORD_AGE_MS = 24 * 60 * 60 * 1000;

/** What a live session leaves in `localStorage` for the next one to find. */
type SessionRecord = {
    schemaVersion: 1;
    sessionId: string;
    who: ClientErrorWho;
    startedAt: number;
    lastSeenAt: number;
    route: string;
    buildVersion: string;
    hidden: boolean;
    activity: string[];
    memory?: ClientMemory;
    peakUsedJsHeapMb?: number;
};

type SessionRecords = Record<string, SessionRecord>;

/**
 * Detects sessions that ended without unloading, and reports them at the next start.
 *
 * Every other diagnostic in this application depends on the page being alive long enough to write
 * something. A tab killed by the operating system for using too much memory — the failure mode large
 * image uploads on a phone actually hit — raises no error, rejects no promise and fires no unload
 * handler. It simply stops, and nothing is ever recorded.
 *
 * The sentinel closes that gap by inverting the test. A live session writes a heartbeat to
 * `localStorage` and clears it on `pagehide`; a record still sitting there at the next start means the
 * session it belonged to died. The record carries the route, the heap and any long-running work in
 * flight at the last heartbeat, which is the evidence the crash itself destroyed.
 *
 * A leftover record is not reported immediately. Another tab may own it, and a backgrounded tab is
 * timer-throttled rather than dead, so the record is watched for {@link CONFIRMATION_DELAY_MS} first
 * and only reported if it never moves. That is the difference between a crash report and a false alarm
 * every time someone opens a second tab.
 */
@Injectable({
    providedIn: 'root',
})
export class SessionSentinelService {
    private readonly errorLog = inject(ClientErrorLogService);
    private readonly authService = inject(AuthService);
    private readonly router = inject(Router);

    private readonly sessionId = this.errorLog.sessionId;
    private readonly startedAt = Date.now();

    private heartbeatTimer: ReturnType<typeof setInterval> | undefined;
    private peakUsedJsHeapMb: number | undefined;
    private storageUsable = true;
    private started = false;

    /**
     * Starts the sentinel.
     *
     * Called from an app initializer rather than the constructor so the first heartbeat lands after the
     * router has a URL to report, and so nothing here runs during dependency construction. Calling it
     * more than once is a no-op.
     */
    start(): void {
        if (this.started || typeof window === 'undefined') {
            return;
        }
        this.started = true;

        this.watchLeftoverRecords();
        this.listen();

        // Only a signed-in session can be reported: without a uid there is no document path to write
        // to. Re-armed on every auth change, so signing in mid-session starts the heartbeat and
        // signing out stops it and takes the record with it.
        this.authService.user$.subscribe(() => {
            const who = this.errorLog.currentWho();
            if (who) {
                this.arm(who);
            } else {
                this.disarm();
            }
        });
    }

    private arm(who: ClientErrorWho): void {
        this.beat(who);

        if (this.heartbeatTimer === undefined) {
            this.heartbeatTimer = setInterval(() => {
                const current = this.errorLog.currentWho();
                if (current) {
                    this.beat(current);
                }
            }, HEARTBEAT_INTERVAL_MS);
        }
    }

    private disarm(): void {
        if (this.heartbeatTimer !== undefined) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = undefined;
        }
        this.clearOwnRecord();
    }

    private listen(): void {
        // `pagehide` is the reliable end-of-life signal on mobile Safari, where `unload` is not fired
        // at all. A session that gets this far ended on purpose and is not a crash.
        window.addEventListener('pagehide', event => {
            this.clearOwnRecord();
            if (!event.persisted) {
                this.disarm();
            }
        });

        // Restored from the back/forward cache: the same session is alive again and must re-arm, or its
        // own next end would look like a crash.
        window.addEventListener('pageshow', event => {
            const who = this.errorLog.currentWho();
            if (event.persisted && who) {
                this.arm(who);
            }
        });

        // A tab is most likely to be discarded while hidden, and timers are throttled there, so take a
        // reading at the moment it goes away rather than relying on the next tick.
        document.addEventListener('visibilitychange', () => this.beatIfArmed());
        this.router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe(() => this.beatIfArmed());
    }

    private beatIfArmed(): void {
        const who = this.errorLog.currentWho();
        if (this.heartbeatTimer !== undefined && who) {
            this.beat(who);
        }
    }

    private beat(who: ClientErrorWho): void {
        const memory = collectMemory();
        if (memory) {
            this.peakUsedJsHeapMb = Math.max(this.peakUsedJsHeapMb ?? 0, memory.usedJsHeapMb);
        }

        this.write(records => ({
            ...records,
            [this.sessionId]: {
                schemaVersion: 1,
                sessionId: this.sessionId,
                who,
                startedAt: this.startedAt,
                lastSeenAt: Date.now(),
                route: this.router.url,
                buildVersion: environment.version,
                hidden: document.visibilityState === 'hidden',
                activity: this.errorLog.activeActivities(),
                memory,
                peakUsedJsHeapMb: this.peakUsedJsHeapMb,
            },
        }));
    }

    /**
     * Picks up records left by other sessions and decides, after a delay, which of them crashed.
     *
     * The delay is what separates a dead session from a throttled one. Anything still unmoved when it
     * expires is reported; anything that moved belongs to a tab that is very much alive.
     */
    private watchLeftoverRecords(): void {
        const candidates = Object.values(this.read()).filter(record => record.sessionId !== this.sessionId);
        if (candidates.length === 0) {
            return;
        }

        setTimeout(() => {
            const current = this.read();

            for (const candidate of candidates) {
                const latest = current[candidate.sessionId];
                if (!latest || latest.lastSeenAt !== candidate.lastSeenAt) {
                    // Gone, or still moving. Either way not a crash this session can report.
                    continue;
                }

                this.write(records => {
                    const { [candidate.sessionId]: _reported, ...rest } = records;
                    return rest;
                });
                this.report(candidate);
            }
        }, CONFIRMATION_DELAY_MS);
    }

    private report(record: SessionRecord): void {
        const age = Date.now() - record.lastSeenAt;
        if (age > MAX_RECORD_AGE_MS) {
            return;
        }

        const who = this.errorLog.currentWho();
        if (who?.uid !== record.who.uid) {
            // The rules only accept an event written under its own uid, so a crash belonging to a user
            // who has not come back cannot be published by whoever is signed in now.
            return;
        }

        const crash: ClientCrashDetails = {
            sessionId: record.sessionId,
            startedAt: new Date(record.startedAt).toISOString(),
            lastSeenAt: new Date(record.lastSeenAt).toISOString(),
            lifetimeMs: record.lastSeenAt - record.startedAt,
            detectedAfterMs: age,
            lastRoute: record.route,
            memory: record.memory,
            peakUsedJsHeapMb: record.peakUsedJsHeapMb,
            hiddenAtLastHeartbeat: record.hidden,
            activity: record.activity,
            buildVersion: record.buildVersion,
        };

        this.errorLog.logCrash(crash, record.who);
    }

    private clearOwnRecord(): void {
        this.write(records => {
            const { [this.sessionId]: _own, ...rest } = records;
            return rest;
        });
    }

    private read(): SessionRecords {
        if (!this.storageUsable) {
            return {};
        }

        try {
            const raw = localStorage.getItem(SENTINEL_KEY);
            const parsed: unknown = raw ? JSON.parse(raw) : {};
            return isRecordMap(parsed) ? parsed : {};
        } catch {
            // Private browsing, disabled storage or corrupt contents. Crash detection is off for this
            // session; it must not take anything else down with it.
            this.storageUsable = false;
            return {};
        }
    }

    private write(update: (records: SessionRecords) => SessionRecords): void {
        if (!this.storageUsable) {
            return;
        }

        try {
            const updated = update(this.read());
            const cutoff = Date.now() - MAX_RECORD_AGE_MS;
            const pruned = Object.fromEntries(Object.entries(updated).filter(([, record]) => record.lastSeenAt > cutoff));

            localStorage.setItem(SENTINEL_KEY, JSON.stringify(pruned));
        } catch {
            this.storageUsable = false;
        }
    }
}

function isRecordMap(value: unknown): value is SessionRecords {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return false;
    }

    return Object.values(value).every(
        entry =>
            !!entry &&
            typeof entry === 'object' &&
            typeof (entry as SessionRecord).sessionId === 'string' &&
            typeof (entry as SessionRecord).lastSeenAt === 'number' &&
            typeof (entry as SessionRecord).who?.uid === 'string',
    );
}
